// frontend/src/components/CampaignManager/tabs/CampaignsTab.tsx
import React, { useState } from 'react';
import {
  Box, Button, Stack, Typography, TableContainer, Table, TableHead, TableRow,
  TableCell, TableBody, Paper, IconButton, Chip, useMediaQuery, useTheme, Alert, Tooltip,
  FormControl, InputLabel, Select, MenuItem, Checkbox, TablePagination
} from '@mui/material';
import { Plus, PencilSimple, Trash, CopySimple } from '@phosphor-icons/react';
import { CampaignCard } from '../MobileCards';
import { Campaign, Lottery } from '../types';
import { formatDate } from '../../../utils/dateUtils';

// Strip lottery suffix from campaign name (e.g. "Inaktive - SKL 147" -> "Inaktive")
const stripLotterySuffix = (name: string): string => {
  if (!name) return name;
  // Match patterns like " - SKL 147", " - NKL 148", " - GKL 12", etc.
  return name.replace(/\s+-\s+(SKL|NKL|GKL)\s+\d+$/i, '').trim();
};

interface Props {
  campaigns: Campaign[];
  lotteries: Lottery[];
  deleteCampaign: (id: number) => Promise<void>;
  onOpenDialog: (campaign: Campaign | null) => void;
  onOpenCopyDialog: (campaign: Campaign) => void;
  onOpenBulkCopyDialog: (campaignIds: number[], sourceLotteryId?: number) => void;
}

export default function CampaignsTab({ campaigns, lotteries, deleteCampaign, onOpenDialog, onOpenCopyDialog, onOpenBulkCopyDialog }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedLotteryId, setSelectedLotteryId] = useState<number | 'all'>('all');
  const [selectedCampaigns, setSelectedCampaigns] = useState<number[]>([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filter campaigns by selected lottery
  const filteredCampaigns = selectedLotteryId === 'all'
    ? campaigns
    : campaigns.filter(c => c.lottery_id === selectedLotteryId);

  // Paginated campaigns
  const paginatedCampaigns = filteredCampaigns.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Kampagne wirklich löschen?')) {
      try {
        await deleteCampaign(id);
      } catch (error) {
        console.error('Error deleting campaign:', error);
      }
    }
  };

  const handleSelectCampaign = (id: number) => {
    setSelectedCampaigns(prev =>
      prev.includes(id)
        ? prev.filter(cId => cId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedCampaigns.length === filteredCampaigns.length) {
      setSelectedCampaigns([]);
    } else {
      setSelectedCampaigns(filteredCampaigns.map(c => c.id));
    }
  };

  const handleBulkCopy = () => {
    const sourceLotteryId = selectedLotteryId !== 'all' ? selectedLotteryId : undefined;
    onOpenBulkCopyDialog(selectedCampaigns, sourceLotteryId);
    setSelectedCampaigns([]);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Kampagnen</Typography>
        <Button
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          startIcon={<Plus size={20} />}
          onClick={() => onOpenDialog(null)}
          sx={{ backgroundColor: '#003366' }}
          disabled={lotteries.length === 0}
        >
          {isSmallMobile ? 'Neu' : 'Neue Kampagne'}
        </Button>
      </Stack>

      {lotteries.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Bitte erstellen Sie zuerst eine Lotterie, bevor Sie eine Kampagne anlegen.
        </Alert>
      )}

      {/* Lotterie Filter and Bulk Actions */}
      {lotteries.length > 0 && (
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ mb: 2 }} alignItems={isMobile ? 'stretch' : 'center'}>
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 250 }}>
            <InputLabel>Lotterie filtern</InputLabel>
            <Select
              value={selectedLotteryId}
              onChange={(e) => {
                setSelectedLotteryId(e.target.value as number | 'all');
                setSelectedCampaigns([]);
                setPage(0);
              }}
              label="Lotterie filtern"
            >
              <MenuItem value="all">
                <em>Alle Lotterien ({campaigns.length})</em>
              </MenuItem>
              {lotteries.map((lottery) => {
                const count = campaigns.filter(c => c.lottery_id === lottery.id).length;
                return (
                  <MenuItem key={lottery.id} value={lottery.id}>
                    {lottery.name} ({lottery.type} {lottery.year}) - {count} Kampagne{count !== 1 ? 'n' : ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {selectedCampaigns.length > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CopySimple size={18} />}
              onClick={handleBulkCopy}
              sx={{ backgroundColor: '#0066cc' }}
            >
              {selectedCampaigns.length} kopieren
            </Button>
          )}
        </Stack>
      )}

      {isMobile ? (
        filteredCampaigns.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {campaigns.length === 0 ? 'Keine Kampagnen vorhanden' : 'Keine Kampagnen für diese Lotterie'}
            </Typography>
          </Paper>
        ) : (
          filteredCampaigns.map((campaign) => (
            <CampaignCard key={campaign.id} campaign={campaign} onEdit={onOpenDialog} onDelete={handleDelete} />
          ))
        )
      ) : (
        <>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={filteredCampaigns.length > 0 && selectedCampaigns.length === filteredCampaigns.length}
                    indeterminate={selectedCampaigns.length > 0 && selectedCampaigns.length < filteredCampaigns.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Kürzel</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Zeitraum</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCampaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {campaigns.length === 0 ? 'Keine Kampagnen vorhanden' : 'Keine Kampagnen für diese Lotterie'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCampaigns.map((campaign) => (
                  <TableRow
                    key={campaign.id}
                    hover
                    selected={selectedCampaigns.includes(campaign.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedCampaigns.includes(campaign.id)}
                        onChange={() => handleSelectCampaign(campaign.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.kurz || '-'}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          minWidth: 40,
                          backgroundColor: campaign.active === false ? '#ffebee' : campaign.status === 'active' ? '#e8f5e9' : undefined,
                          textDecoration: campaign.active === false ? 'line-through' : undefined,
                          opacity: campaign.active === false ? 0.7 : 1
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <strong style={{ opacity: campaign.active === false ? 0.6 : 1, textDecoration: campaign.active === false ? 'line-through' : undefined }}>
                        {stripLotterySuffix(campaign.name)}
                      </strong>
                      {campaign.active === false && (
                        <Chip label="deaktiviert" size="small" sx={{ ml: 1, fontSize: '0.65rem', height: 18 }} color="warning" />
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(campaign.start_date)}
                    </TableCell>
                    <TableCell><Chip label={campaign.status} size="small" /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Bearbeiten">
                        <IconButton size="small" onClick={() => onOpenDialog(campaign)}>
                          <PencilSimple size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="In andere Lotterie kopieren">
                        <IconButton size="small" onClick={() => onOpenCopyDialog(campaign)} color="primary">
                          <CopySimple size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton size="small" color="error" onClick={() => handleDelete(campaign.id)}>
                          <Trash size={18} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredCampaigns.length > 0 && (
          <TablePagination
            component="div"
            count={filteredCampaigns.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Zeilen pro Seite:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
          />
        )}
        </>
      )}
    </Box>
  );
}
